import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../lib/api';
import { Home, ChevronRight } from 'lucide-react';

const CmsPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setPage(null);
    axios.get(`${API}/pages/${slug}`)
      .then((r) => {
        setPage(r.data);
        document.title = `${r.data.title} — TelanganaMart`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <nav className="flex items-center gap-1 text-xs text-gray-500 mb-4" aria-label="breadcrumb">
          <Link to="/" className="hover:text-[#ee5e23] inline-flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-700 dark:text-gray-300">{page?.title || slug}</span>
        </nav>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 animate-pulse">
            <div className="h-8 w-1/2 bg-gray-200 rounded mb-4" />
            <div className="h-3 w-full bg-gray-100 rounded mb-2" />
            <div className="h-3 w-5/6 bg-gray-100 rounded mb-2" />
            <div className="h-3 w-4/6 bg-gray-100 rounded" />
          </div>
        ) : notFound ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="cms-not-found">
            <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
            <p className="text-gray-600 mt-2">The page you&apos;re looking for doesn&apos;t exist or has been moved.</p>
            <Link to="/" className="text-[#ee5e23] hover:underline mt-4 inline-block">← Back to home</Link>
          </div>
        ) : page && (
          <article className="bg-white border border-gray-200 rounded-lg p-6 sm:p-10 prose prose-sm sm:prose max-w-none
            prose-headings:text-gray-900 prose-headings:font-bold
            prose-h1:text-3xl prose-h1:mb-4 prose-h1:text-[#2c54b2]
            prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
            prose-h3:text-lg
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-[#ee5e23] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900
            prose-ul:my-3 prose-li:my-1
            prose-hr:border-gray-200"
            data-testid={`cms-page-${page.slug}`}>
            <ReactMarkdown
              components={{
                a: ({ href, children }) => (
                  href?.startsWith('/') ? <Link to={href}>{children}</Link> : <a href={href} target="_blank" rel="noreferrer">{children}</a>
                ),
              }}
            >{page.content || '_This page is empty._'}</ReactMarkdown>
            <p className="text-xs text-gray-400 mt-8 pt-4 border-t border-gray-100">
              Last updated {new Date(page.updated_at || page.created_at).toLocaleDateString()}
            </p>
          </article>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CmsPage;
